import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook for tracking route transitions and showing page progress
 * 
 * Provides state for showing a progress bar during route transitions,
 * with configurable minimum display time to prevent flickering.
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.minDisplayTime - Minimum time to show progress (ms), default 300
 * @param {number} options.animationDuration - Duration of fade out animation (ms), default 200
 * @returns {Object} Hook return values
 */
export const useRouteProgress = (options = {}) => {
  const {
    minDisplayTime = 300,
    animationDuration = 200
  } = options;

  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previousLocation, setPreviousLocation] = useState(location);

  // Track route changes
  useEffect(() => {
    if (location.pathname !== previousLocation.pathname) {
      // Route is changing - start loading
      setIsLoading(true);
      setProgress(0);

      // Quickly animate to 30%
      const quickInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 30) {
            clearInterval(quickInterval);
            return 30;
          }
          return prev + 10;
        });
      }, 50);

      // Slowly animate to 90%
      const slowInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(slowInterval);
            return 90;
          }
          if (prev >= 30) {
            return prev + 5;
          }
          return prev;
        });
      }, 100);

      // Complete after minimum display time
      const completeTimeout = setTimeout(() => {
        clearInterval(quickInterval);
        clearInterval(slowInterval);
        setProgress(100);
        
        // Hide after animation
        setTimeout(() => {
          setIsLoading(false);
          setProgress(0);
          setPreviousLocation(location);
        }, animationDuration);
      }, minDisplayTime);

      return () => {
        clearInterval(quickInterval);
        clearInterval(slowInterval);
        clearTimeout(completeTimeout);
      };
    }
  }, [location, previousLocation, minDisplayTime, animationDuration]);

  // Manual control methods
  const startProgress = useCallback(() => {
    setIsLoading(true);
    setProgress(0);
    
    // Animate to 90%
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const completeProgress = useCallback(() => {
    setProgress(100);
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, animationDuration);
  }, [animationDuration]);

  const resetProgress = useCallback(() => {
    setIsLoading(false);
    setProgress(0);
  }, []);

  return {
    isLoading,
    progress,
    startProgress,
    completeProgress,
    resetProgress
  };
};

export default useRouteProgress;
